import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { fg } from '@confluence/feature-gating';
import {
	useContentByLabelDataQuery,
	type useContentByLabelDataQuery$data,
	type useContentByLabelDataQuery$variables,
} from '@confluence/content-by-label-macro/entry-points/useContentByLabelDataQuery.ts';
import { composeMacroParamsForQuery } from '@confluence/content-by-label-macro/entry-points/composeMacroParamsForQuery.ts';

import type {
	MacroContentNode,
	MacroPreloadContext,
	MacroPreloadHandler,
} from '../preloadMacroData.ts';

/**
 * Preload handler for the content-by-label macro.
 *
 * For a contentbylabel extension node found in the ADF content, this handler
 * builds query variables from the macro parameters and fires the
 * useContentByLabelDataQuery GraphQL query to populate the Relay store.
 */
export const contentByLabelPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'contentbylabel',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const variables = composeMacroParamsForQuery(
			node.attrs?.parameters?.macroParams,
			context.spaceKey,
			context.contentId,
		);
		if (!variables.cql) {
			return;
		}
		await slowQueryGuard(
			'contentByLabelPreload',
			() =>
				query<useContentByLabelDataQuery$data, useContentByLabelDataQuery$variables>({
					query: useContentByLabelDataQuery,
					variables: {
						...variables,
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						skipSearchTitle: fg('cc_rewrite_macros_use_content_not_search_title'),
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						skipContentTitle: !fg('cc_rewrite_macros_use_content_not_search_title'),
					},
					context: { single: true },
				}),
			SLOW_QUERY_DEFAULT_TIMEOUT,
		);
	},
};
